import { ENDPOINT, VEHICLES } from 'src/constants/api';

import { baseApi } from '../base';

const unwrapApiData = <T>(response: unknown): T => {
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    (response as { data?: unknown }).data !== undefined
  ) {
    return (response as { data: T }).data;
  }

  return response as T;
};

export type ImageProcessingBatchStatus =
  | 'processing'
  | 'awaiting_review'
  | 'completed'
  | 'canceled';

export type ImageProcessingBatchItemStatus =
  | 'pending'
  | 'queued'
  | 'processing'
  | 'ready'
  | 'applied'
  | 'failed'
  | 'discarded'
  | 'expired';

export type ImageProcessingBatchCounts = {
  pending: number;
  queued: number;
  processing: number;
  ready: number;
  applied: number;
  failed: number;
  discarded: number;
  expired: number;
};

export type ImageProcessingBatchItem = {
  job_id: number;
  media_id: number;
  status: ImageProcessingBatchItemStatus;
  result_url?: string;
  result_expires_at?: string;
  created_at: string;
  updated_at: string;
  error?: {
    code: string;
    message: string;
  };
};

export type ImageProcessingBatchSkipReason =
  | 'not_found'
  | 'not_an_image'
  | 'already_enhanced'
  | 'processing_already_active'
  | 'invalid_source';

export type ImageProcessingBatchSkip = {
  media_id: number;
  reason: ImageProcessingBatchSkipReason;
};

export type ImageProcessingBatchVehicle = {
  id: number;
  title: string;
  thumbnail: string;
  vin?: string;
};

export type ImageProcessingBatch = {
  id: string;
  vehicle_id: number;
  mode: 'enhancement';
  status: ImageProcessingBatchStatus;
  total: number;
  counts: ImageProcessingBatchCounts;
  items: ImageProcessingBatchItem[];
  skipped: ImageProcessingBatchSkip[];
  created_at: string;
  updated_at: string;
  canceled_at: string | null;
  completed_at: string | null;
  status_url: string;
  /** Only returned by the account-wide endpoint; vehicle-scoped responses omit it. */
  vehicle?: ImageProcessingBatchVehicle;
};

export type CreateImageProcessingBatchParams = {
  vehicleId: number;
  mediaIds: number[];
  idempotencyKey: string;
};

export type BatchSelectionParams = {
  vehicleId: number;
  batchId: string;
  jobIds?: number[];
  scope?: 'ready';
};

export type BatchItemOperationResult = {
  job_id: number;
  status: ImageProcessingBatchItemStatus | 'error';
  error?: {
    code: string;
    message: string;
  };
};

export type BatchItemsOperationResponse = {
  batch: ImageProcessingBatch;
  results: BatchItemOperationResult[];
};

export type BatchParams = {
  vehicleId: number;
  batchId: string;
};

export const accountBatchesPath = () => `${ENDPOINT}/image-processing/batches`;

export const vehicleBatchesPath = (vehicleId: number) =>
  `${ENDPOINT}/${VEHICLES}/${vehicleId}/image-processing/batches`;

const LOCKED_ITEM_STATUSES: ImageProcessingBatchItemStatus[] = [
  'pending',
  'queued',
  'processing',
  'ready',
];

/**
 * Media stays locked until its batch item reaches a terminal state. `ready` is included so a
 * photo with an enhancement waiting for review can't be deleted or enhanced individually,
 * which would leave the batch pointing at media that no longer matches.
 */
export const isLockedBatchItemStatus = (status: ImageProcessingBatchItemStatus) =>
  LOCKED_ITEM_STATUSES.includes(status);

export const getBatchProgress = (batch: ImageProcessingBatch) => {
  const completed =
    batch.counts.ready +
    batch.counts.applied +
    batch.counts.failed +
    batch.counts.discarded +
    batch.counts.expired;

  return { completed, total: batch.total };
};

export const isOpenBatchStatus = (status: ImageProcessingBatchStatus) =>
  status === 'processing' || status === 'awaiting_review';

const imageProcessingBatchesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOpenImageProcessingBatches: builder.query<ImageProcessingBatch[], void>({
      query: () => ({
        url: `${accountBatchesPath()}?status=open`,
        method: 'GET',
      }),
      transformResponse: unwrapApiData<ImageProcessingBatch[]>,
    }),
    createImageProcessingBatch: builder.mutation<
      ImageProcessingBatch,
      CreateImageProcessingBatchParams
    >({
      query: ({ vehicleId, mediaIds, idempotencyKey }) => ({
        url: vehicleBatchesPath(vehicleId),
        method: 'POST',
        body: { media_ids: mediaIds, mode: 'enhancement' },
        headers: {
          'Idempotency-Key': idempotencyKey,
        },
      }),
      transformResponse: unwrapApiData<ImageProcessingBatch>,
    }),
    applyImageProcessingBatchItems: builder.mutation<
      BatchItemsOperationResponse,
      BatchSelectionParams
    >({
      query: ({ vehicleId, batchId, jobIds, scope }) => ({
        url: `${vehicleBatchesPath(vehicleId)}/${batchId}/apply`,
        method: 'POST',
        body: jobIds ? { job_ids: jobIds } : { scope },
      }),
      transformResponse: unwrapApiData<BatchItemsOperationResponse>,
    }),
    discardImageProcessingBatchItems: builder.mutation<
      BatchItemsOperationResponse,
      BatchSelectionParams
    >({
      query: ({ vehicleId, batchId, jobIds, scope }) => ({
        url: `${vehicleBatchesPath(vehicleId)}/${batchId}/discard`,
        method: 'POST',
        body: jobIds ? { job_ids: jobIds } : { scope },
      }),
      transformResponse: unwrapApiData<BatchItemsOperationResponse>,
    }),
    cancelImageProcessingBatch: builder.mutation<ImageProcessingBatch, BatchParams>({
      query: ({ vehicleId, batchId }) => ({
        url: `${vehicleBatchesPath(vehicleId)}/${batchId}/cancel`,
        method: 'POST',
      }),
      transformResponse: unwrapApiData<ImageProcessingBatch>,
    }),
  }),
});

export const {
  useLazyGetOpenImageProcessingBatchesQuery,
  useCreateImageProcessingBatchMutation,
  useApplyImageProcessingBatchItemsMutation,
  useDiscardImageProcessingBatchItemsMutation,
  useCancelImageProcessingBatchMutation,
} = imageProcessingBatchesApi;

export default imageProcessingBatchesApi;
